import * as React from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import { alpha } from "@mui/material/styles";
import { COLORS } from "../../theme/theme-options";
import type { ServiceTemplate } from "../../types";
import DialogContext from "../../contexts/DialogContext";
import { useServiceTemplates } from "../../hooks/query/useServiceTemplates";
import RowActionsMenu from "../../components/RowActionsMenu";

const templateIconByType: Record<ServiceTemplate["type"], React.ReactNode> = {
  saas: <LayersRoundedIcon fontSize="small" />,
  retail: <Inventory2RoundedIcon fontSize="small" />,
  banking: <AccountBalanceRoundedIcon fontSize="small" />,
  internal_it: <BuildRoundedIcon fontSize="small" />,
};

const templateColorByType: Record<ServiceTemplate["type"], string> = {
  saas: "#06b6d4",
  retail: "#10b981",
  banking: "#f59e0b",
  internal_it: "#64748b",
};

const SERVICE_TEMPLATE_SKELETON_COUNT = 4;

const ServiceTemplateCardSkeleton: React.FC = () => {
  return (
    <Card variant="glass" sx={{ p: 2.2, flex: 1, display: "flex" }}>
      <Stack spacing={1.7} sx={{ flex: 1 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: 2 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={0.8} alignItems="center">
                <Skeleton variant="rounded" width={170} height={28} />
                <Skeleton variant="rounded" width={60} height={22} />
              </Stack>
              <Skeleton variant="rounded" width={100} height={19} sx={{ mt: 0.3 }} />
            </Box>
          </Stack>
          <Skeleton variant="circular" width={24} height={24} />
        </Stack>

        <Box>
          <Skeleton variant="rounded" width="92%" height={20} />
          <Skeleton variant="rounded" width="72%" height={20} sx={{ mt: 0.5 }} />
        </Box>

        <Box>
          <Skeleton variant="rounded" width={96} height={16} sx={{ mb: 0.8 }} />
          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton
                key={`kpi-chip-skeleton-${index}`}
                variant="rounded"
                width={78}
                height={24}
              />
            ))}
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
};

const ServiceTemplatesConfigPage: React.FC = () => {
  const { data: templates = [], isLoading } = useServiceTemplates();
  const { show } = React.useContext(DialogContext);
  const [rowMenu, setRowMenu] = React.useState<{
    anchorEl: HTMLElement;
    template: ServiceTemplate;
  } | null>(null);

  const sourceByTemplateId = React.useMemo(() => {
    return new Map(
      templates.map((template) => [
        template.id,
        template.system ? "system" : "custom",
      ]),
    );
  }, [templates]);

  return (
    <Box display="flex" flexDirection="column" gap={2.5}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: COLORS.grey[100] }}>
            Service Templates
          </Typography>
          <Typography sx={{ color: COLORS.grey[500], mt: 0.2 }}>
            Default templates from constants plus your custom templates
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<AddRoundedIcon fontSize="small" />}
          onClick={() => {
            show({ dialog: "new-service-template" });
          }}
        >
          New
        </Button>
      </Stack>

      <Grid container spacing={2}>
        {isLoading
          ? Array.from({ length: SERVICE_TEMPLATE_SKELETON_COUNT }).map((_, index) => (
              <Grid key={`service-template-skeleton-${index}`} size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
                <ServiceTemplateCardSkeleton />
              </Grid>
            ))
          : templates.map((template) => {
              const icon = templateIconByType[template.type];
              const color = templateColorByType[template.type];
              const source = sourceByTemplateId.get(template.id) ?? "custom";

              return (
                <Grid key={template.id} size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
                  <Card variant="glass" sx={{ p: 2.2, flex: 1, display: "flex" }}>
                    <Stack spacing={1.7} sx={{ flex: 1 }}>
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              display: "grid",
                              placeItems: "center",
                              color,
                              border: `1px solid ${alpha(color, 0.4)}`,
                              background: `linear-gradient(135deg, ${alpha(color, 0.2)}, ${alpha(color, 0.12)})`,
                            }}
                          >
                            {icon}
                          </Box>
                          <Box>
                            <Stack direction="row" spacing={0.8} alignItems="center">
                              <Typography sx={{ fontSize: 18, fontWeight: 700, color: COLORS.grey[100] }}>
                                {template.name}
                              </Typography>
                              {source === "system" ? (
                                <Chip size="small" color="info" label="System" />
                              ) : (
                                <Chip size="small" label="Custom" />
                              )}
                            </Stack>
                            <Typography sx={{ color: COLORS.grey[500], mt: 0.2 }}>
                              {template.type.replaceAll("_", " ")}
                            </Typography>
                          </Box>
                        </Stack>
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            setRowMenu({
                              anchorEl: event.currentTarget,
                              template,
                            });
                          }}
                        >
                          <MoreVertRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>

                      <Typography sx={{ color: COLORS.grey[300] }}>
                        {template.description}
                      </Typography>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 0.8,
                            color: COLORS.grey[500],
                            mb: 0.8,
                          }}
                        >
                          DEFAULT KPIS
                        </Typography>
                        <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                          {template.kpis.map((kpi) => (
                            <Chip
                              key={`${template.id}-${kpi.id}`}
                              size="small"
                              label={kpi.name}
                              sx={{
                                bgcolor: alpha(COLORS.grey[700], 0.35),
                                color: COLORS.grey[300],
                                border: `1px solid ${alpha(COLORS.grey[500], 0.2)}`,
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    </Stack>
                  </Card>
                </Grid>
              );
            })}
      </Grid>

      <RowActionsMenu
        anchorEl={rowMenu?.anchorEl ?? null}
        open={Boolean(rowMenu)}
        onClose={() => setRowMenu(null)}
        disableEdit={rowMenu ? rowMenu.template.system : true}
        disableDelete={rowMenu ? rowMenu.template.system : true}
        disableEditReason="System service templates are read-only."
        disableDeleteReason="System service templates cannot be deleted."
        onEdit={() => {
          const serviceTemplateId = rowMenu?.template.id;
          if (!serviceTemplateId) {
            return;
          }
          show({
            dialog: "edit-service-template",
            serviceTemplateId,
          });
        }}
        onDelete={() => {
          const serviceTemplateId = rowMenu?.template.id;
          if (!serviceTemplateId) {
            return;
          }
          show({
            dialog: "confirm-delete",
            entity: "service-template",
            ids: serviceTemplateId,
          });
        }}
      />
    </Box>
  );
};

export default ServiceTemplatesConfigPage;
